import { type ErrorInfo, type ReactNode } from 'react';

import { AlertCircle } from 'lucide-react';
import {
  ErrorBoundary as ReactErrorBoundary,
  type FallbackProps,
} from 'react-error-boundary';

import { Alert, AlertDescription, AlertTitle } from './ui/alert';
import { Button } from './ui/button';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: (error: Error, resetError: () => void) => ReactNode;
}

/**
 * Default fallback component for error boundary
 * Displays a user-friendly error message with options to retry or reload
 */
const DefaultFallback = ({ error, resetErrorBoundary }: FallbackProps) => {
  if (import.meta.env.DEV) {
    console.error('Error caught by boundary:', error);
  }

  const handleReload = () => {
    window.location.reload();
  };

  return (
    <div className="flex items-center justify-center min-h-screen p-4">
      <div className="max-w-md w-full">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Something went wrong</AlertTitle>
          <AlertDescription className="mt-4 space-y-4">
            <p>
              An unexpected error occurred. Please try refreshing the page or
              contact support if the problem persists.
            </p>
            {import.meta.env.DEV && error instanceof Error && (
              <div className="mt-4 p-2 bg-black/10 rounded text-xs font-mono overflow-auto max-h-40">
                {error.message}
              </div>
            )}
            <div className="flex gap-2 mt-4">
              <Button onClick={resetErrorBoundary} variant="outline" size="sm">
                Try Again
              </Button>
              <Button onClick={handleReload} variant="default" size="sm">
                Reload Page
              </Button>
            </div>
          </AlertDescription>
        </Alert>
      </div>
    </div>
  );
};

/**
 * Global Error Boundary Component
 * Uses react-error-boundary library to catch unhandled errors in the React component tree
 *
 * Library: react-error-boundary
 * @see https://github.com/bvaughn/react-error-boundary
 */
export const ErrorBoundary = ({ children, fallback }: ErrorBoundaryProps) => {
  const fallbackRender = fallback
    ? ({ error, resetErrorBoundary }: FallbackProps) => {
        // Ensure error is an Error instance before passing to custom fallback
        const errorInstance =
          error instanceof Error ? error : new Error(String(error));
        return fallback(errorInstance, resetErrorBoundary);
      }
    : DefaultFallback;

  const handleError = (error: Error, errorInfo: ErrorInfo) => {
    // Log error to console in development
    if (import.meta.env.DEV) {
      console.error('Error caught by boundary:', error, errorInfo);
    }

    // In production, you could send this to an error tracking service
    // Example: Sentry.captureException(error, { contexts: { react: errorInfo } });
  };

  return (
    <ReactErrorBoundary fallbackRender={fallbackRender} onError={handleError}>
      {children}
    </ReactErrorBoundary>
  );
};
